import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { combineLatest, distinctUntilChanged, filter, map, Observable } from 'rxjs';

import { Cabin, FinnairAmount, SeatInfo, SeatmapSvgAndControlData, SeatPrice } from '@fcom/dapi/api/models';
import { AIRCRAFT_TRANSLATION_MAP } from '@fcom/common/interfaces/seat-map.interface';
import {
  ButtonSize,
  ChipTheme,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  ModalButtons,
} from '@fcom/ui-components';
import { equals, isPresent, uniqueBy } from '@fcom/core/utils';
import { CmsArticle } from '@fcom/core-api';
import { FragmentService } from '@fcom/common/services';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes } from '@fcom/common/interfaces';

import { getSeatMapFlight, getSeatPrice, getSeatType } from '../../../utils';
import { FlightWithStreams, SeatMapTraveler, seatTypeInfo } from '../../../interfaces';

export interface SeatChipData {
  article$: Observable<CmsArticle>;
  className: string;
  label: string;
  icon: string;
  originalPrice: FinnairAmount;
  price: FinnairAmount;
  seat: SeatInfo;
  seatType: {
    className: string;
    label: string;
    cmsKey: string;
  };
}

@Component({
  selector: 'fin-seat-map-seatchips',
  templateUrl: './seat-map-seatchips.component.html',
  styleUrls: ['./seat-map-seatchips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeatMapSeatChipsComponent implements OnInit {
  @Input() flight: FlightWithStreams;
  @Input() passengers$: Observable<SeatMapTraveler[]>;
  @Input() seatMapElement: ElementRef;

  @Output() scrollToSeat: EventEmitter<string> = new EventEmitter();

  availableSeatTypes$: Observable<SeatChipData[]>;
  availableSeatTypesInModal: SeatChipData[];

  readonly ButtonSize = ButtonSize;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconLibrary = IconLibrary;
  readonly IconPosition = IconPosition;
  readonly ModalButtons = ModalButtons;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ChipTheme = ChipTheme;

  seatIconCache: Record<string, string> = {};
  seatTypeModalOpen = false;

  constructor(
    private fragmentService: FragmentService,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.availableSeatTypes$ = combineLatest([this.passengers$, this.flight.seatMap$]).pipe(
      filter(([passengers, seatMap]) => Array.isArray(passengers) && isPresent(seatMap)),
      distinctUntilChanged(
        ([prevPassengers, prevSeatMapData], [nextPassengers, nextSeatMapData]) =>
          equals(prevSeatMapData, nextSeatMapData) && equals(prevPassengers, nextPassengers)
      ),
      map(([passengers, seatMapData]) => {
        const activePax = passengers.find((pax) => pax.active);
        const hasInfant = passengers.some((pax) => pax.withInfant);
        const selectedSeats = passengers
          .map((pax) => {
            return pax.seatNumber;
          })
          .filter(Boolean);

        return this.getSeatTypesFromData(activePax.id, hasInfant, seatMapData, selectedSeats);
      })
    );
  }

  getSeatTypesFromData(
    paxId: string,
    hasInfant: boolean,
    seatMap: SeatmapSvgAndControlData,
    selectedSeats: string[]
  ): SeatChipData[] {
    let seatTypeChips: SeatChipData[] = [];

    seatMap.seats
      .filter((seat) => {
        return seat.statuses[paxId] === 'available';
      })
      .forEach((seat) => {
        const seatType = getSeatType(seat.characteristics);
        const seatPrice = getSeatPrice(seat, paxId);

        const seatsOfSameTypeAndPrice = seatTypeChips.filter((addedSeat) => {
          const isSameSeatType = addedSeat.seatType.className === seatType.className;

          if (!isSameSeatType) {
            return false;
          }

          return addedSeat.price?.amount === seatPrice?.price?.amount;
        });

        if (seatsOfSameTypeAndPrice.length) {
          seatTypeChips = this.showSelectedSeatOnlyWhenLastOneOfThatTypeAndPrice(
            seat,
            seatType,
            seatPrice,
            selectedSeats,
            seatTypeChips
          );
        } else {
          seatTypeChips.push({
            ...seatType,
            article$: this.fragmentService.getFragment<CmsArticle>(this.getFragmentUrl(seatMap, seatType.cmsKey)),
            icon: this.getIconFromSeatMap(seat),
            price: getSeatPrice(seat, paxId).price,
            originalPrice: getSeatPrice(seat, paxId).originalPrice,
            seat,
            seatType,
          });
        }
      });

    // if none of the passengers has an infant, we filter out baby bassinet seat chips
    if (!hasInfant) {
      seatTypeChips = seatTypeChips.filter((chip) => chip.seatType.className !== seatTypeInfo.B.className);
    }

    // First, sort by type and price
    seatTypeChips.sort((a, b) => {
      if (a.className === b.className) {
        return parseFloat(a.price.amount) - parseFloat(b.price.amount);
      }
      return a.className.localeCompare(b.className);
    });

    // Calculate the minimum price for each type
    const minPriceByType = this.findMinPriceByType(seatTypeChips);

    // Sort the array based on the type and price
    seatTypeChips.sort((a, b) => {
      if (minPriceByType[a.className] === minPriceByType[b.className]) {
        return a.className.localeCompare(b.className);
      }
      return Number(minPriceByType[a.className]) - Number(minPriceByType[b.className]);
    });

    this.availableSeatTypesInModal = seatTypeChips.filter(uniqueBy((seatTypeChip) => seatTypeChip.className));

    return seatTypeChips;
  }

  findMinPriceByType(arr: SeatChipData[]): Record<string, string> {
    return arr.reduce((minPriceByType, item) => {
      if (!minPriceByType[item.className] || item.price.amount < minPriceByType[item.className]) {
        minPriceByType[item.className] = item.price.amount;
      }
      return minPriceByType;
    }, {});
  }

  /**
   * For performance reasons we pass in the seatType and seatPrice, so we don't figure out the values again.
   *
   * Because this is called for every seat (so for example 300 seats on the A350) it can be quite heavy.
   */
  showSelectedSeatOnlyWhenLastOneOfThatTypeAndPrice(
    seat: SeatInfo,
    seatType: SeatChipData['seatType'],
    seatPrice: SeatPrice,
    selectedSeats: string[],
    seatTypeChips: SeatChipData[]
  ): SeatChipData[] {
    const seatToReplace = seatTypeChips.find((seatTypeChip) => {
      const isSameSeatType = seatTypeChip.seatType.className === seatType.className;

      return (
        isSameSeatType &&
        seatTypeChip.price?.amount === seatPrice?.price?.amount &&
        selectedSeats.includes(seatTypeChip.seat.seatNumber)
      );
    });

    if (!seatToReplace) {
      return seatTypeChips;
    }

    return seatTypeChips.map((seatTypeChip) => {
      if (seatToReplace.seat.seatNumber === seatTypeChip.seat.seatNumber) {
        seatToReplace.seat = seat;
        seatToReplace.icon = this.getIconFromSeatMap(seat);
      }

      return seatTypeChip;
    });
  }

  /**
   * Extract from the main SVG a copy of the seat icon for a specific seat.
   *
   * Removes the A tag from the seat and keeps everything else.
   * Removes xy coordinates from the seat element.
   */
  getIconFromSeatMap(seat: SeatInfo): string {
    if (this.seatIconCache[seat.seatNumber]) {
      return this.seatIconCache[seat.seatNumber];
    }

    const element: SVGGElement = this.seatMapElement.nativeElement
      .querySelector(`[data-id="${seat.seatNumber}"]`)
      .cloneNode(true);

    const aTag = element.querySelector('a');

    Array.from(aTag.childNodes).forEach((node) => {
      if ((node as SVGGElement).classList.contains('seat-selected')) {
        return;
      }

      element.append(node);
    });
    element.removeAttribute('transform');
    element.removeChild(aTag);

    this.seatIconCache[seat.seatNumber] = element.outerHTML;

    return this.seatIconCache[seat.seatNumber];
  }

  handleClick(seatData: SeatChipData): void {
    this.scrollToSeat.emit(seatData.seat.seatNumber);
    this.gtmService.seatMapEvent(
      `seat-type-${seatData.seatType.cmsKey}`,
      ElementTypes.BUTTON,
      'chip',
      ElementActions.CLICK,
      {
        flightInfo: getSeatMapFlight(this.flight),
      }
    );
  }

  openModal(): void {
    this.seatTypeModalOpen = true;
    this.gtmService.seatMapEvent('seat-type-info-modal', ElementTypes.MODAL, '', ElementActions.VIEW, {
      flightInfo: getSeatMapFlight(this.flight),
    });
  }

  closeModal(): void {
    this.seatTypeModalOpen = false;
  }

  private getFragmentUrl(seatMap: SeatmapSvgAndControlData, characteristicKey: string): string {
    const cabinClass = Cabin[this.flight.cabinClass];
    const aircraftType = AIRCRAFT_TRANSLATION_MAP[seatMap.aircraftCode];
    return `fragments.seatTypeInfos.${aircraftType}.${cabinClass.toLowerCase()}.${characteristicKey}.url`;
  }
}
